import { Component, OnInit, signal, computed } from '@angular/core';
import { of } from 'rxjs';
import { switchMap, first } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { Meter, ConsentStatus, ShareInfo } from '../../models/meter.model';
import { AddMeterDialogComponent } from '../meter-details/add-meter-dialog/add-meter-dialog.component';
import { SharePartnerDialogComponent } from '../meter-details/share-partner-dialog/share-partner-dialog.component';
import { UtilService } from '../../services/util.service';
import { MeterService } from '../../services/meter.service';
import { LogService } from '../../services/log.service';
import { ConsentDialogService } from '../../services/consent-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Partner } from '../../models/partner.model';
import { ConsentService } from '../../services/consent.service';
import { ConsentRequest, RelationshipType, SharedConsent } from '../../models/consent.model';
import { MeterCardEnhancedComponent } from './meter-card-enhanced/meter-card-enhanced.component';
import { getRelationshipDisplayText, getRelationshipIcon } from '../../utils/meter.utils';
import { ManageSharesDialogComponent } from './manage-shares-dialog/manage-shares-dialog.component';
import { WithdrawConsentDialogComponent } from './withdraw-consent-dialog/withdraw-consent-dialog.component';
import { DownloadConfirmDialogComponent, DownloadConfirmResult } from './download-confirm-dialog/download-confirm-dialog.component';

export interface MeterGroup {
  address: string;
  meters: Meter[];
  relationship?: RelationshipType;
}

@Component({
  selector: 'app-meter-details',
  standalone: true,
  imports: [
    CommonModule, 
    MatCardModule, 
    MatButtonModule, 
    MatIconModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    FormsModule,
    MeterCardEnhancedComponent
  ],
  templateUrl: './meter-details.component.html',
  styleUrl: './meter-details.component.scss'
})
export class MeterDetailsComponent implements OnInit {

  // Using signals for reactive state management
  meters = signal<Meter[]>([]);
  isLoading = signal<boolean>(true);
  selectedMeterNumbers = signal<Set<string>>(new Set());
  searchQuery = signal<string>('');
  
  // Pagination
  pageIndex = signal<number>(0);
  pageSize = signal<number>(10); // 10 addresses per page

  // Computed signal to filter meters based on search query
  filteredMeters = computed<Meter[]>(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.meters();
    }

    return this.meters().filter(meter => {
      // Search across multiple properties
      return (
        meter.meterNumber.toLowerCase().includes(query) ||
        meter.address.toLowerCase().includes(query) ||
        meter.fuelType.toLowerCase().includes(query) ||
        meter.meterType.toLowerCase().includes(query) ||
        meter.consentStatus.toLowerCase().includes(query) ||
        (meter.postCode && meter.postCode.toLowerCase().includes(query)) ||
        (meter.relationship && getRelationshipDisplayText(meter.relationship).toLowerCase().includes(query))
      );
    });
  });

  // Computed signal to get selected meters
  selectedMeters = computed<Meter[]>(() => {
    const selectedNumbers = this.selectedMeterNumbers();
    return this.filteredMeters().filter(meter => selectedNumbers.has(meter.meterNumber));
  });

  // Computed signal to group meters by address
  allMeterGroups = computed<MeterGroup[]>(() => {
    const metersValue = this.filteredMeters();
    const groupMap = new Map<string, { originalAddress: string, meters: Meter[], relationship?: RelationshipType }>();

    // Group meters by address (case insensitive)
    metersValue.forEach(meter => {
      const addressKey = meter.address.toLowerCase();
      if (!groupMap.has(addressKey)) {
        groupMap.set(addressKey, { originalAddress: meter.address, meters: [], relationship: meter.relationship });
      } else {
        // If relationship is not set yet, try to get it from this meter
        const group = groupMap.get(addressKey)!;
        if (!group.relationship && meter.relationship) {
          group.relationship = meter.relationship;
        }
      }
      groupMap.get(addressKey)!.meters.push(meter);
    });

    // Convert map to array and sort meters within each group
    return Array.from(groupMap.entries()).map(([key, group]) => ({
      address: group.originalAddress,
      meters: group.meters.sort((a, b) => a.meterNumber.localeCompare(b.meterNumber)),
      relationship: group.relationship
    })).sort((a, b) => a.address.localeCompare(b.address));
  });

  // Computed signal for paginated meter groups
  paginatedMeterGroups = computed<MeterGroup[]>(() => {
    const allGroups = this.allMeterGroups();
    const startIndex = this.pageIndex() * this.pageSize();
    const endIndex = startIndex + this.pageSize();
    return allGroups.slice(startIndex, endIndex);
  });

  // Computed signal for total number of groups (for pagination)
  totalGroups = computed<number>(() => {
    return this.allMeterGroups().length;
  });

  // Computed signal to check if all meters are selected (on current page)
  allMetersSelected = computed<boolean>(() => {
    const totalMeters = this.filteredMeters().length;
    const selectedCount = this.selectedMeterNumbers().size;
    return totalMeters > 0 && selectedCount === totalMeters;
  });

  // Computed signal to check if some (but not all) meters are selected
  someMetersSelected = computed<boolean>(() => {
    const selectedCount = this.selectedMeterNumbers().size;
    const totalMeters = this.filteredMeters().length;
    return selectedCount > 0 && selectedCount < totalMeters;
  });

  // Expose utility functions for template
  getRelationshipDisplayText = getRelationshipDisplayText;
  getRelationshipIcon = getRelationshipIcon;

  // TrackBy function for performance
  trackByGroup(index: number, group: MeterGroup): string {
    return group.address;
  }

  trackByMeter(index: number, meter: Meter): string {
    return meter.meterNumber;
  }

  constructor(
    private dialog: MatDialog,
    private utilService: UtilService,
    private meterService: MeterService,
    private consentService: ConsentService,
    private consentDialogService: ConsentDialogService,
    private route: ActivatedRoute,
    private router: Router,
    private logService: LogService
  ) {}

  loadMeters(): void {
    this.isLoading.set(true);
    this.meterService.getMeters().subscribe({
      next: (meters) => {
        this.meters.set(meters);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading meters:', error);
        this.isLoading.set(false);
      }
    });
  }

  ngOnInit(): void {
    // Log page view
    this.logService.logPageView('meter-details', {
      queryParams: this.route.snapshot.queryParams
    });

    // Load meters from service
    this.loadMeters();

    this.route.queryParamMap.subscribe(params => {
      const consentRequestId = params.get('consentRequestId');
      const addMeter = params.get('addMeter') === 'true';

      if (consentRequestId) {
        this.handleConsentRequest(consentRequestId);
      } else if (addMeter) {
        this.addNewMeter();
      }
    });
  }

  // Meter selection methods
  onMeterSelectionChange(meterNumber: string, selected: boolean): void {
    const currentSelection = new Set(this.selectedMeterNumbers());
    if (selected) {
      currentSelection.add(meterNumber);
    } else {
      currentSelection.delete(meterNumber);
    }
    this.selectedMeterNumbers.set(currentSelection);
  }

  toggleAllSelection(): void {
    if (this.allMetersSelected()) {
      // Deselect all
      this.selectedMeterNumbers.set(new Set());
    } else {
      // Select all filtered meters
      const allMeterNumbers = new Set(this.filteredMeters().map(m => m.meterNumber));
      this.selectedMeterNumbers.set(allMeterNumbers);
    }
  }

  clearSelection(): void {
    this.selectedMeterNumbers.set(new Set());
  }

  // Search methods
  onSearchChange(query: string): void {
    this.searchQuery.set(query);
    // Reset to first page when search changes
    this.pageIndex.set(0);
  }

  clearSearch(): void {
    this.searchQuery.set('');
    // Reset to first page when search is cleared
    this.pageIndex.set(0);
  }

  // Pagination methods
  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  // Action methods
  async addNewMeter(): Promise<void> {
    console.log('Add new meter clicked');
    
    const addMeterResponse = await this.openAddMeterDialog(false);

    if (addMeterResponse === null) {
      console.warn('User cancelled adding meters');
      return;
    }
      
    // this.shareMeter(addMeterResponse.meters[0], addMeterResponse.requestId);
  }

  bulkShareMeters(): void {
    console.log('Bulk share meters clicked', this.selectedMeters());
    this.shareMeters(this.selectedMeters());
  }

  bulkWithdrawConsent(): void {
    console.log('Bulk withdraw consent clicked', this.selectedMeters());
    this.withdrawConsents(this.selectedMeters());
  }

  downloadAllMeters(): void {
    // The meter-data-sharing API accepts all MPxNs in one request, which
    // produces a single download for the customer's complete meter list.
    const meterNumbers = [...new Set(this.meters().map(meter => meter.meterNumber).filter(Boolean))];

    if (meterNumbers.length === 0) {
      return;
    }

    const loadingPopup = this.utilService.openCloseablePopup(
      'Preparing Download',
      'Preparing your download, please wait...',
      true
    );

    this.consentService.generateShareableLink('link', meterNumbers).pipe(
      switchMap((link: string) => this.consentService.pollDownloadReady(link))
    ).subscribe({
      next: (link: string) => {
        loadingPopup.close();
        this.triggerDownload(link);
      },
      error: (error) => {
        loadingPopup.close();
        console.error('Error generating download link:', error);
        this.utilService.showErrorMessage('Download Failed', 'Failed to generate download link. Please try again.');
      }
    });
  }

  async shareMeter(meter: Meter, requestId?: string, consentRequest?: ConsentRequest): Promise<void> {
    console.log('Share meter clicked for:', meter.meterNumber);
    await this.shareMeters([meter], consentRequest, requestId);
  }

  downloadMeter(meter: Meter): void {
    let loadingPopup: MatDialogRef<any> | undefined;
    this.consentService.getSharedConsentsByMeter(meter.meterNumber).pipe(
      first(),
      switchMap((consents: SharedConsent[]) => {
        const existing = consents.find(
          c => c.shareType === 'link' && c.consentStatus.toUpperCase() === 'ACTIVE'
        );

        if (!existing) {
          return of('latest' as DownloadConfirmResult);
        }

        return this.utilService.openPopupCustom<DownloadConfirmResult, { existingConsent: SharedConsent }>(
          DownloadConfirmDialogComponent,
          { existingConsent: existing },
          '600px'
        );
      }),
      switchMap((result: DownloadConfirmResult | undefined) => {
        if (!result) {
          return of(null);
        }

        if (result === 'same') {
          const existingLink$ = this.consentService.getSharedConsentsByMeter(meter.meterNumber).pipe(
            first(),
            switchMap((consents: SharedConsent[]) => {
              const existing = consents.find(c => c.shareType === 'link' && c.consentStatus.toUpperCase() === 'ACTIVE');
              return of(existing?.dataLink ?? null);
            })
          );
          return existingLink$.pipe(
            switchMap(link => {
              if (!link) return of(null);
              loadingPopup = this.utilService.openCloseablePopup('Preparing Download', 'Preparing your download, please wait...', true);
              return this.consentService.pollDownloadReady(link);
            })
          );
        }

        loadingPopup = this.utilService.openCloseablePopup('Preparing Download', 'Preparing your download, please wait...', true);
        return this.consentService.generateShareableLink('link', [meter.meterNumber]).pipe(
          switchMap((link: string) => this.consentService.pollDownloadReady(link))
        );
      })
    ).subscribe({
      next: (link: string | null) => {
        loadingPopup?.close();
        if (!link) return;
        this.triggerDownload(link);
      },
      error: (error) => {
        loadingPopup?.close();
        console.error('Error generating download link:', error);
        this.utilService.showErrorMessage('Download Failed', 'Failed to generate download link. Please try again.');
      }
    });
  }

  private triggerDownload(link: string): void {
    const anchor = document.createElement('a');
    anchor.href = link;
    anchor.download = '';
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  withdrawConsent(meter: Meter): void {
    console.log('Withdraw consent clicked for:', meter.meterNumber);
    this.withdrawConsents([meter]);
  }

  onShareRevoked(): void {
    // Refresh meters list when a share is revoked
    this.loadMeters();
  }

  revokeShare(share: ShareInfo, meter: Meter): void {
    // Confirmation already shown in child component, just handle API call
    this.consentService.revokeAccess(share.shareId, [meter.meterNumber]).subscribe((success: boolean) => {
      if (success) {
        console.log(`Access successfully revoked for share ID: ${share.shareId} (meter: ${meter.meterNumber} from ${share.shareeName})`);
        this.utilService.openSnackBar('Access successfully revoked', 'Close', 'success');
        // Refresh meters list
        this.loadMeters();
      } else {
        console.error(`Failed to revoke access for share ID: ${share.shareId}`);
        this.utilService.openSnackBar('Failed to revoke access', 'Close', 'error');
      }
    });
  }

  manageShares(meter: Meter): void {
    console.log('Manage shares clicked for:', meter.meterNumber);
    const dialogRef = this.dialog.open(ManageSharesDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { meter }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'revoke') {
        // User confirmed in dialog, now handle API call
        const share = result.share;
        this.consentService.revokeAccess(share.shareId, [meter.meterNumber]).subscribe((success: boolean) => {
          if (success) {
            console.log(`Access successfully revoked for share ID: ${share.shareId} (meter: ${meter.meterNumber} from ${share.shareeName})`);
            this.utilService.openSnackBar('Access successfully revoked', 'Close', 'success');
            // Refresh meters list
            this.loadMeters();
          } else {
            console.error(`Failed to revoke access for share ID: ${share.shareId}`);
            this.utilService.openSnackBar('Failed to revoke access', 'Close', 'error');
          }
        });
      }
    });
  }

  withdrawConsents(meters: Meter[]): void {
    // Open the elaborate withdraw consent dialog
    const dialogRef = this.dialog.open(WithdrawConsentDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: { meters }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'confirm') {
        // User confirmed withdrawal
        this.meterService.withdrawConsent(meters.map(m => m.meterNumber)).subscribe({
          next: (success) => {
            if (success) {
              this.utilService.openSnackBar(
                `Consent for ${meters.length} meter(s) has been successfully withdrawn`, 
                'Close', 
                'success'
              );
              // Refresh the meters list to reflect the change
              this.loadMeters();
              // Clear selection after successful withdrawal
              this.clearSelection();
            }
          },
          error: (error) => {
            console.error('Error withdrawing consent:', error);
            this.utilService.showErrorMessageFromAPIResponse('Failed to withdraw consent.', error);
          }
        });
      } else if (result?.action === 'manage-shares') {
        // User chose to manage shares instead
        this.manageShares(result.meter);
      }
    });
  }

  // Private methods from original component
  private async handleConsentRequest(consentRequestId: string): Promise<void> {
    try {
      // Open the consent dialog with the request ID
      const dialogRef = this.consentDialogService.openConsentDialog(
        consentRequestId, 
        'Process Consent Request'
      );

      // Handle the dialog result
      const result: {status: 'SUCCESS' | 'FAILURE' | 'SHARE_INCOMPLETE', message: any} = await dialogRef.afterClosed().toPromise();

      if (result) {
        console.log('Consent dialog completed with result:', result);

        // Refresh meters after consent processing
        this.loadMeters();

        let routeUrl = 'meter-details';
        if (result.status === 'SHARE_INCOMPLETE') {
          const consentRequest: {meters: Meter[], consentRequest: ConsentRequest, requestId: string | null} = result.message;
          // Initiate manual share flow
          // await this.shareMeters(consentRequest.meters, consentRequest.consentRequest, consentRequest.requestId ?? undefined);
          routeUrl = 'meter-details';
          return; // No further action needed
        }

        // Clear query params from URL
        this.router.navigate([routeUrl], {
          queryParams: { consentRequestId: null },
          queryParamsHandling: 'merge'
        });
        
      } else {
        console.log('Consent dialog was closed without completion');
        
        // Clear query params from URL even if dialog was cancelled
        this.router.navigate([], {
          queryParams: { consentRequestId: null },
          queryParamsHandling: 'merge'
        });
      }
      
    } catch (error) {
      console.error('Error opening consent dialog:', error);
      this.utilService.openSnackBar('Failed to open consent dialog', 'Error', 'error');
      
      // Clear query params from URL on error
      this.router.navigate([], {
        queryParams: { consentRequestId: null },
        queryParamsHandling: 'merge'
      });
    }
  }

  private createPartnerFromConsentRequest(consentRequest: ConsentRequest): Partner | null {
    if (consentRequest.agentOrgId || consentRequest.agentOrg || consentRequest.agentEmail) {
      const partner: Partner = {
        name: consentRequest.agentOrg || '',
        email: consentRequest.agentEmail || undefined,
        id: consentRequest.agentOrgId || ''
      };

      // Set agent context
      this.consentService.agentContext = partner;
      return partner;
    }
    return null;
  }

  private openAddMeterDialog(isMultiMeter = false, consentRequest?: ConsentRequest, scope?: 'ANNUAL' | 'HH'): Promise<{requestId?: string, meters: Meter[], skippedMeters: Meter[]} | null> {
    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(AddMeterDialogComponent, {
        width: '800px',
        disableClose: true,
        data: { 
          isMultiMeter,
          consentRequest: consentRequest,
          scope
        }
      });

      dialogRef.afterClosed().subscribe((result: {requestId: string, meters: Meter[], skippedMeters: Meter[]} | {declined: boolean} | null) => {
        if (result) {
          if ('declined' in result) {
            // User declined the dialog
            resolve(null);

            //Remove query params from URL
            this.router.navigate([], {
              queryParams: { consentRequestId: null },
              queryParamsHandling: 'merge'
            });
            return;
          }

          const addedMeters = result.meters;

          // Refresh the meters list to show any changes
          this.meterService.getMeters().subscribe({
            next: (meters) => {
              this.meters.set(meters);

              console.log(`Successfully added ${addedMeters.length} meters:`, addedMeters.map(m => m.meterNumber));
              resolve(result);
            },
            error: (error) => {
              console.error('Error refreshing meters after add:', error);
              reject(error);
            }
          });
        } else {
          // User cancelled the dialog
          resolve(null);
        }
      });
    });
  }

  private async shareMeters(meters: Meter[], consentRequest?: ConsentRequest, requestId?: string, hasSkippedMeters = false): Promise<boolean> {
    // Create Partner object from consent request details
    if (consentRequest) {
      const shareWith = this.createPartnerFromConsentRequest(consentRequest);
      if (!shareWith) {
        console.error('No valid partner found in consent request');
        return Promise.resolve(false);
      }

      console.log(`Sharing ${meters.length} meters with ${shareWith.name}:`, meters.map(m => m.meterNumber));
    }

    return new Promise((resolve) => {
      const dialogRef = this.dialog.open(SharePartnerDialogComponent, {
        width: '600px',
        data: { 
          meters: meters,
          isNewlyAdded: !!consentRequest,
          consentRequest,
          requestId,
          hasSkippedMeters
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && result.shared) {
          const meterCount = meters.length;
          const meterText = meterCount === 1 ? 'meter' : 'meters';
          this.utilService.openSnackBar(
            `${meterCount} ${meterText} successfully shared with ${result.partner.name}`, 
            'Close', 
            'success'
          );
          
          // Clear selection after successful sharing
          this.clearSelection();
          resolve(true);
        } else {
          resolve(false);
        }

        if (result?.cancelled) {
         // Skip reload
        } else {
         this.loadMeters(); // Refresh meters list after sharing
        }

        this.consentService.agentContext = null; // Clear agent context after sharing
      });
    });
  }
}
